import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ShouldRevalidateFunction } from 'react-router';
import type { CapiPlp, GridSize } from 'types/capi/plp';
import * as uuid from 'uuid';

import ArrowIcon from '~/assets/icons/Arrow';
import Breadcrumbs from '~/components/BreadCrumbs';
import Content from '~/components/Content';
import DownloadAppBlock from '~/components/DownloadAppBlock';
import HorizontalLine from '~/components/HorizontalLine';
import PopularReleasesCarousel from '~/components/PopularReleasesCarousel';
import RecommendedArticles from '~/components/RecommendedArticles';
import PlpViewEvent from '~/events/PlpViewEvent';
import SearchViewEvent from '~/events/SearchViewEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import useViewEvent from '~/hooks/useViewEvent';

import Advert from './components/Advert';
import FilterOptions from './components/FilterOptions';
import Menus from './components/Menus/Menus';
import { TopModuleSection } from './components/ModuleSection';
import NotificationTooltip from './components/NotificationTooltip';
import Products from './components/Products';
import SubMenu from './components/SubMenu';
import { getPlpViewProperties } from './plpViewProperties';

const POPULAR_CAROUSEL_MODULE_TYPE = 'plp-popular-carousel';
const POPULAR_CAROUSEL_MODULE_POSITION = 1;

export default function PlpView({
  allowNotifications = true,
  showAppBanner = true
}: {
  allowNotifications?: boolean;
  showAppBanner?: boolean;
  showPromotion?: boolean;
}) {
  const plp = usePageData<CapiPlp>();

  const isUpcoming = plp.filter_options.some(
    (o) => o.text === 'Upcoming' && o.active
  );

  const hasSizeMenu = plp.menus.some((m) => m.id === 'SIZE_MENU');

  const user = useUser();
  const { clientIp, isBot, session } = useRootData();
  const [gridSize, setGridSize] = useState<GridSize>('small');

  const { t } = useTranslation('plp');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const listId = useMemo(() => uuid.v4(), [plp]);

  useStructuredData(plp._schemas);

  useViewEvent({
    viewProperties: useMemo(() => getPlpViewProperties(plp), [plp]),
    viewType: 'plp'
  });

  useEffect(() => {
    const isSearch = new URLSearchParams(window.location.search).has('q');

    const eventData = {
      clientIp: clientIp ?? undefined,
      isBot,
      listId,
      plp,
      user
    };

    const viewEvent = isSearch
      ? new SearchViewEvent(eventData)
      : new PlpViewEvent(eventData);

    viewEvent.send();
  }, [clientIp, plp, session, user, isBot, listId]);

  const sizeSelected = plp.menus.some((m) => m.id === 'SIZE_MENU' && m.active);

  return (
    <>
      {plp.advert && <Advert advert={plp.advert} />}
      <TopModuleSection />
      <div className="flex w-full flex-col overflow-x-clip bg-grey-tile">
        <div className="content-width w-full py-4">
          <div className="hidden w-full pb-2 md:flex">
            <Breadcrumbs breadcrumbs={plp.breadcrumbs} />
          </div>
          <div className="grid grid-cols-3 items-center">
            <div className="col-span-full flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-0 md:py-0">
              <div className="flex flex-col gap-1">
                <h1 className="text-title-md">{plp.title}</h1>
                {plp.content && plp.content.length > 0 && (
                  <div className="text-sm !font-normal leading-[20px] md:hidden">
                    <Content
                      content={[plp.content[0]!]}
                      collapsibleParagraphs
                    />
                  </div>
                )}
              </div>
              {plp.filter_options.length > 0 && (
                <FilterOptions options={plp.filter_options} />
              )}
            </div>
            {plp.sub_title && (
              <p className="col-span-full flex text-sm md:col-span-2 md:pb-0">
                {plp.sub_title}
              </p>
            )}
          </div>
          {plp.content && plp.content.length > 0 && (
            <div className="hidden text-start text-sm font-normal leading-[22px] text-grey-read md:block md:w-2/3 lg:w-3/4">
              <Content content={[plp.content[0]!]} collapsibleParagraphs />
            </div>
          )}
        </div>
        <div className="content-width w-full md:py-1">
          <div className="flex w-full items-center justify-between gap-2">
            {plp.menus.length > 0 && (
              <div className="no-scrollbar -mx-4 flex items-center gap-2 overflow-scroll px-4 pb-4 pt-2 md:mx-0 md:overflow-auto md:px-0">
                <Menus menus={plp.menus} />
              </div>
            )}
            <div className={`${isUpcoming ? 'block' : 'hidden md:block'} `}>
              <SubMenu setGridSize={setGridSize} gridSize={gridSize} />
            </div>
          </div>

          <div className="pb-4 pt-2">
            <HorizontalLine />
          </div>

          <div className="flex items-center justify-between">
            {!sizeSelected && hasSizeMenu && (
              <div className="flex items-center gap-2 overflow-hidden bg-[#FFAA0029] px-2 py-[5px]">
                <ArrowIcon
                  className="shrink-0 rotate-90"
                  height="16"
                  width="16"
                />
                <span className="truncate text-[11px] leading-5">
                  {t('selectYourSize')}
                </span>
              </div>
            )}

            <div className={`${!isUpcoming ? 'block md:hidden' : 'hidden'}`}>
              <SubMenu setGridSize={setGridSize} gridSize={gridSize} />
            </div>
          </div>
          {isUpcoming && allowNotifications && (
            <div className="flex justify-start">
              <NotificationTooltip gridSize={gridSize} />
            </div>
          )}
          <Products
            allowNotifications={allowNotifications}
            feature={
              plp.popular_products && (
                <PopularReleasesCarousel
                  products={plp.popular_products}
                  title="Most popular releases"
                  description="The pairs with most notifications set in the last 7 days"
                  modulePosition={POPULAR_CAROUSEL_MODULE_POSITION}
                  moduleType={POPULAR_CAROUSEL_MODULE_TYPE}
                  pagePosition="PLP Popular Carousel"
                  pageType="plp"
                  allowNotifications
                  showBackground
                />
              )
            }
            gridSize={gridSize}
            listId={listId}
            modulePosition={
              plp.popular_products
                ? POPULAR_CAROUSEL_MODULE_POSITION + 1
                : POPULAR_CAROUSEL_MODULE_POSITION
            }
          />
        </div>
      </div>
      <div className="bg-concrete">
        {plp.recent_articles && (
          <RecommendedArticles
            articles={plp.recent_articles}
            viewMoreNewsPath={plp.view_more_news_path}
          />
        )}
        {plp.content && plp.content.length > 0 && (
          <div className="content-width flex flex-col gap-8 py-8 lg:py-12 [&_p]:text-sm [&_p]:text-grey-read">
            <Content content={plp.content.slice(1)} />
          </div>
        )}
      </div>
      {showAppBanner && (
        <DownloadAppBlock
          downloadLink="https://thesolesupplier.onelink.me/BPkz/8481irlk"
          qrCode="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/download_app_homepage_qr_code.png"
          pagePosition="PLP App Banner"
        />
      )}
    </>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};
